import {
  AsyncGrid,
  AsyncLoadingSection,
  ButtonCore,
  ButtonStyle,
  ColSize,
  EmptyHeaderCell,
  FileDownloadCell,
  GridProvider,
  MainArea,
  PageCard,
  PageCardRow,
  PageCardSection,
  Render,
  ThemeColor,
  WrappableCell,
  buildEndpoint,
  defaultDataTransform,
  defaultGridProps,
  useDataFetcher,
  useIdParam,
  useSubGrid,
} from "@techsharegov/react-ui";
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { HtmlAgendaModel } from "./Models";
import { AgendaEndpoints } from "Models/Routes";
import {
  GridCellProps,
  GridColumn,
  GridDetailRowProps,
} from "@progress/kendo-react-grid";
import {
  ConditionalDetailColumnCell,
  FileDownloadButtonCell,
} from "Components";
import { Button, Col, Row } from "reactstrap";

export const HtmlAgenda: React.FC = () => {
  return (
    <Fragment>
      <HtmlAgendaHeader />
    </Fragment>
  );
};

export const HtmlAgendaHeader: React.FC = () => {
  const [id] = useIdParam();
  const [data, loadingStatus] = useDataFetcher<HtmlAgendaModel>(
    buildEndpoint(
      AgendaEndpoints.meetings.controllerActionString("getHtmlAgenda"),
      id
    ),
    new HtmlAgendaModel(),
    defaultDataTransform,
    HtmlAgendaModel
  );
  const [youtubeUrl, setYoutubeUrl] = useState<string>();
  const [preMeetingText, setPreMeetingText] = useState<string>("");

  useEffect(() => {
    if (data?.preMeetingText !== undefined) {
      setPreMeetingText(data?.preMeetingText);
    }
    if (data?.videoId != null) {
      setYoutubeUrl("https://www.youtube.com/embed/" + data?.videoId);
    }
  }, [data]);

  return (
    <AsyncLoadingSection loadingStatus={loadingStatus}>
      <PageCard>
        <PageCardRow>
          <PageCardSection
            columnWidth={{
              xs: data.videoId != null ? ColSize.TwoThirds : ColSize.Full,
            }}
            noTitleMargin
            noTopPadding
          >
            <Row>
              <Col xs={ColSize.OneThird}>
                <div className="county-logo-container">
                  <div
                    className="county-logo"
                    style={{
                      backgroundImage: `url(data:image/png;base64,${data?.logo})`,
                    }}
                  />
                </div>
              </Col>
              <Col xs={ColSize.TwoThirds}>
                <div className="html-agenda-header">
                  <span>{data.countyName}</span>
                  <br />
                  <span>{data.meetingName}</span>
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={ColSize.OneThird}></Col>
              <Col xs={ColSize.TwoThirds}>
                <div className="html-agenda-meeting-header whitespace-preline">
                  <span>{data.meetingHeader}</span>
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={ColSize.OneThird}></Col>

              <Col xs={ColSize.TwoThirds}>
                <div className="html-agenda-meeting-start whitespace-preline">
                  {data.meetingStartDisplay}
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={ColSize.Full}>
                <div
                  id="PreMeetingText"
                  className="html-agenda-pre-meeting-text whitespace-preline"
                  dangerouslySetInnerHTML={{ __html: preMeetingText }}
                ></div>
              </Col>
            </Row>
            <MainArea>
              <GridProvider>
                <HtmlAgendaGrid
                  meetingId={id}
                  setYoutubeUrl={setYoutubeUrl}
                  videoId={data?.videoId}
                />
              </GridProvider>
            </MainArea>
          </PageCardSection>
          <Render condition={data?.videoId != null}>
            <PageCardSection columnWidth={{ xs: ColSize.OneThird }}>
              <iframe
                width={"100%"}
                height="360"
                src={youtubeUrl}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            </PageCardSection>
          </Render>
        </PageCardRow>
      </PageCard>
    </AsyncLoadingSection>
  );
};

export type HtmlAgendaGridProps = {
  meetingId: string | undefined;
  videoId: string | undefined;
  setYoutubeUrl: Dispatch<SetStateAction<string | undefined>>;
};

export const HtmlAgendaGrid: React.FC<HtmlAgendaGridProps> = ({
  meetingId,
  setYoutubeUrl,
  videoId,
}) => {
  const search = {
    id: meetingId,
  };
  const { expandField } = useSubGrid();

  return (
    <AsyncGrid
      {...defaultGridProps}
      isMainGrid
      readEndpoint={buildEndpoint(
        AgendaEndpoints.meetings.controllerActionString(
          "readHtmlAgendaSections"
        )
      )}
      search={search}
      sortable={false}
      groupable={false}
      detail={FilesDetailGrid}
      expandField={expandField}
      pageSize={500}
      className="html-agenda-grid"
    >
      <GridColumn
        field="expanded"
        cell={DetailColumnCell}
        width="30px"
        headerCell={EmptyHeaderCell}
      />
      <GridColumn
        field="fullDisplay"
        title="Subject"
        cell={(props) =>
          SubjectCellWrapper({ meetingId, videoId, setYoutubeUrl, ...props })
        }
      />
      <GridColumn
        field="id"
        title=" "
        width="auto"
        cell={FilePacketDownloadCell}
      />
    </AsyncGrid>
  );
};

const DetailColumnCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <ConditionalDetailColumnCell
      condition={props?.dataItem?.hasAttachments}
      matchField="id"
      {...props}
    />
  );
};

const FilesDetailGrid: React.FC<GridDetailRowProps> = ({ dataItem }) => {
  return (
    <GridProvider>
      <AsyncGrid
        readEndpoint={AgendaEndpoints.meetingattachments.controllerActionString(
          "readByActionItem"
        )}
        pageSize={200}
        search={{
          meetingId: dataItem.meetingId,
          actionItemId: dataItem.actionItemId,
        }}
        className="html-attachments-grid"
      >
        <GridColumn field="id" title=" " width="auto" cell={FileViewCell} />
        <GridColumn
          field="id"
          title=" "
          width="auto"
          cell={FileNameDownloadCell}
        />
      </AsyncGrid>
    </GridProvider>
  );
};

const FileViewCell: React.FC<GridCellProps> = ({ ...props }) => {
  return (
    <FileDownloadButtonCell
      endpoint={AgendaEndpoints.actionitemattachments.controllerActionString(
        "download?id=" + props.dataItem.id
      )}
      title="Download"
      entityName="Packet"
      {...props}
    />
  );
};

const FileNameDownloadCell: React.FC<GridCellProps> = ({ ...props }) => {
  return (
    <FileDownloadCell
      fileControllerEndpoint={AgendaEndpoints.actionitemattachments.base}
      filename={
        props.dataItem.fileNameWithoutExtension +
        "." +
        props.dataItem.container +
        "." +
        props.dataItem.fileId +
        props.dataItem.extension
      }
      display={props.dataItem.fileNameWithoutExtension}
      {...props}
    />
  );
};

const SubjectCellWrapper: React.FC<GridCellProps & HtmlAgendaGridProps> = ({
  dataItem,
  setYoutubeUrl,
  videoId,
}) =>
  videoId != null && dataItem?.timeStamp != null ? (
    <>
      <WrappableCell>
        <span
          className="k-button-link-base k-button-link"
          style={{ cursor: "pointer" }}
          onClick={() =>
            setYoutubeUrl(
              "https://www.youtube.com/embed/" +
                videoId +
                "?start=" +
                dataItem.timeStamp +
                "&autoplay=1"
            )
          }
        >
          <SubjectCellContent dataItem={dataItem} />
        </span>
      </WrappableCell>
    </>
  ) : (
    <>
      <WrappableCell>
        <SubjectCellContent dataItem={dataItem} />
      </WrappableCell>
    </>
  );

const SubjectCellContent: React.FC<{ dataItem: any }> = ({ dataItem }) => (
  <div className={"grid-tabs-" + dataItem.tabs}>    
    <div dangerouslySetInnerHTML={{ __html: dataItem.fullDisplay}}></div>
    <Render
      condition={
        dataItem.itemDescription !== undefined &&
        dataItem.itemDescription !== null &&
        dataItem.itemDescription !== ""
      }
    >
      <br />
      <i dangerouslySetInnerHTML={{ __html: dataItem.itemDescription}}></i>
    </Render>
    <Render
      condition={
        dataItem.staffRecommendationDisplay !== undefined &&
        dataItem.staffRecommendationDisplay !== null &&
        dataItem.staffRecommendationDisplay !== ""
      }
    >
      <div dangerouslySetInnerHTML={{ __html: dataItem.staffRecommendationDisplay}}></div>
    </Render>
  </div>
);

const FilePacketDownloadCell: React.FC<GridCellProps> = ({ ...props }) => {
  return props.dataItem.hasPacketAttachment ? (
    <FileDownloadButtonCell
      endpoint={AgendaEndpoints.actionitemattachments.controllerActionString(
        "downloadPacket?actionItemId=" + props.dataItem.actionItemId
      )}
      title="Download"
      entityName="Packet"
      {...props}
    />
  ) : (
    <Fragment></Fragment>
  );
};
